import * as React from "react";
import { useSelector } from "react-redux";
import { Button, Typography, Stack, Box, useMediaQuery, Dialog, DialogContent, Stepper, Step, StepLabel } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { AmountTier, AutoPayType, CreateScheduledAutoPayDto, FeesDTO, FlatAutopayFeeDto, PaymentClassEnum, TieredAutopayFeeDto } from "@inosio/payments-sdk";
import { RecurringModalContentsReturnType } from "./RecurringModalContents";
import { createTransDetailRecords, getFrequency } from "./UseSharedMethods";
import ModalContentsPayMethods from "./ModalContentsPayMethods";
import { DEFAULT_ADHOC_BANK_UUID, DEFAULT_ADHOC_CARD_UUID } from "../../../pmui/residents/saga/pmResidentsSlice";
import { selectAdhocBankAccount, selectAdhocBankCard } from "../../../pmui/residents/saga/pmResidentsSlice";
import { MaskedInputState } from "../../../shared/MaskedInput";
import { selectBalanceDetails, selectResidentDetails } from "../sagas/residentpaymentsSlice";
import { convertSqlDateToUSCalendarDate } from "../../../shared/util/converters";

const useStyles = makeStyles({
  modalContainer: {
    boxShadow: "none",
    borderRadius: "24px",
  },
  globalError: {
    display: "flex",
    flexDirection: "row",
    marginBottom: "30px",
    borderTop: "2px  solid #DB3939",
    borderRadius: "24px",
    padding: "12px 20px",
    width: "100%",
    height: "49px",
  },
});

export interface CreateApDto extends CreateScheduledAutoPayDto {
  RawFeeDto?: FeesDTO;
}

interface PropsType {
  open: boolean;
  maskedInputValues: MaskedInputState;
  savedAutoPayValues: RecurringModalContentsReturnType;
  closeCallback: () => void;
  backCallback: () => void;
  cancelCallback: () => void;
  continueCallback: (val: { createApDto: CreateApDto, feesCents: number, lastFour: string, tiers?: AmountTier[] }) => void;
}

export default function RecurringPayMethodsModal(props: PropsType) {
  const classes = useStyles();
  const isOnMobileDevice = useMediaQuery("(max-width:600px)");
  const adhocBankAccount = useSelector(selectAdhocBankAccount);
  const adhocBankCard = useSelector(selectAdhocBankCard);
  const currentBalanceInfo = useSelector(selectBalanceDetails);
  const residentsDetails = useSelector(selectResidentDetails);

  const { maskedInputValues, savedAutoPayValues, closeCallback, backCallback, cancelCallback, continueCallback } = props;
  const [lastFour, setLastFour] = React.useState('');
  const [selectedTiers, setSelectedTiers] = React.useState<AmountTier[]>([]);
  const [paymentFeesCents, setPaymentFeesCents] = React.useState(0);
  const [createApDto, setCreateApDto] = React.useState<CreateApDto>({} as CreateApDto);
  const irpPaymentClass = savedAutoPayValues.Type === AutoPayType.Variable ? PaymentClassEnum.Vap: PaymentClassEnum.Fap;

  const setSelectedPaymentHandler = (feesCents: number, lastFour: string, methodUuid: string, tiers?: AmountTier[], rawFeeDTO?: FeesDTO) => {
    if (methodUuid && rawFeeDTO) {
      setLastFour(lastFour);
      if (!!tiers) setSelectedTiers(tiers);
      setPaymentFeesCents(feesCents);

      const createAutoPayDto: CreateApDto = {
        ...savedAutoPayValues,
        PayerUuid: residentsDetails?.Uuid,
        BankAccount: methodUuid === DEFAULT_ADHOC_BANK_UUID ? adhocBankAccount : undefined,
        BankCard: methodUuid === DEFAULT_ADHOC_CARD_UUID ? adhocBankCard : undefined,
        RawFeeDto: rawFeeDTO,
        FeeSchedule:
          irpPaymentClass === PaymentClassEnum.Vap
            ? tiers?.length === 0
              ? ({ Amount: feesCents, PaidBy: rawFeeDTO?.PaidBy } as FlatAutopayFeeDto)
              : ({ AmountTiers: tiers, PaidBy: rawFeeDTO?.PaidBy } as TieredAutopayFeeDto)
            : ({ Amount: feesCents, PaidBy: rawFeeDTO?.PaidBy } as FlatAutopayFeeDto), //also takes care of VAps with flat fee
        ScheduledAutoPayDetails: 
          irpPaymentClass === PaymentClassEnum.Fap
            ? createTransDetailRecords(maskedInputValues, currentBalanceInfo, methodUuid)
            : [
                {
                  Amount: 0,
                  PropertyPaymentTypeId: 1,
                  FromPayMethodUuid: methodUuid,
                },
              ],
      };

      setCreateApDto(createAutoPayDto);
    }
  }

  const handleContinue = () => {
    continueCallback({ createApDto, feesCents: paymentFeesCents, lastFour, tiers: selectedTiers });
  }

  const disableRecurringContinue = !createApDto.PayerUuid;

  return (
    <div id="payment-modal" className={classes.modalContainer}>
      <Dialog
        fullScreen={isOnMobileDevice}
        open={props.open}
        onClose={closeCallback}
        fullWidth={true}
        hideBackdrop={true}
        disableEnforceFocus
        PaperProps={{
          style: {
            pointerEvents: "auto",
            height: !isOnMobileDevice ? "700px" : "100%",
            borderRadius: !isOnMobileDevice ? "24px" : undefined,
            boxSizing: "border-box",
          },
        }}
      >
        <DialogContent>
          <Stack direction="column" justifyContent="center" alignItems="flex-start" spacing={2} style={{ marginTop: "24px" }}>
            <Box sx={{ width: "100%" }}>
              <Stepper activeStep={1}>
                {[1, 2].map((step, index) => {
                  return (
                    <Step key={index}>
                      <StepLabel></StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>

            <div>
              <Typography
                style={{
                  color: "#14142B",
                  fontSize: "18px",
                  lineHeight: "22px",
                  fontWeight: 700,
                  fontFamily: "'Open Sans', sans-serif",
                  marginTop: "5px",
                  marginBottom: "20px",
                }}
              >
                Which Pay Method?{" "}
              </Typography>
            </div>
            
            <Box sx={{ width: "100%", paddingRight: "20px" }}>
              <ModalContentsPayMethods 
                irpPaymentClass={`${irpPaymentClass}`}
                setSelectedPaymentHandler={setSelectedPaymentHandler}
                targetDate={+savedAutoPayValues.StartDate.split('-')[2]}
              />
            </Box>

            <Typography
              style={{
                fontSize: "12px",
                lineHeight: "22px",
                fontFamily: "'Work Sans', sans-serif",
              }}
            >
              {savedAutoPayValues.Type === AutoPayType.Fixed
                ? `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(savedAutoPayValues.StartDate)} and every ${getFrequency(savedAutoPayValues.Frequency)} thereafter for $${(((savedAutoPayValues.FixedAmount ?? 0) + paymentFeesCents) / 100).toFixed(2)} 
                ${!savedAutoPayValues.EndDate ? "until you cancel it" : `with the final payment on ${convertSqlDateToUSCalendarDate(savedAutoPayValues.EndDate ?? '')}`}`
                : `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(savedAutoPayValues.StartDate)} and every ${getFrequency(savedAutoPayValues.Frequency)} thereafter for your full balance on file ${
                  !savedAutoPayValues.VariableMaxAmountAllowed ? "" : ` up to $${((savedAutoPayValues.VariableMaxAmountAllowed ?? 0) / 100).toFixed(2)}`
                  }  + fee ${!savedAutoPayValues.EndDate ? "until you cancel it" : `with the final payment on ${convertSqlDateToUSCalendarDate(savedAutoPayValues.EndDate ?? '')}`}`}
            </Typography>

            <Typography id="confirm-amount">Note: It may take 2-3 business days before bank transfers are shown on your bank statement</Typography>
            
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" spacing={2} style={{ width: "100%", marginBottom: '20px' }}>
              <Button
                style={{
                  width: "45px",
                  fontFamily: "'Work Sans', sans-serif",
                  borderRadius: "24px",
                  height: "33px",
                  textTransform: "capitalize",
                  fontSize: "14px",
                  lineHeight: "18px",
                  color: "#14142B",
                }}
                size="small"
                onClick={backCallback}
              >
                Back
              </Button>
              <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" spacing={2} style={{ width: "100%" }}>
                <Button
                  style={{
                    width: "45px",
                    fontFamily: "'Work Sans', sans-serif",
                    borderRadius: "24px",
                    height: "33px",
                    textTransform: "capitalize",
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#14142B",
                  }}
                  size="small"
                  onClick={cancelCallback}
                >
                  Cancel
                </Button>
                <Button
                  disabled={disableRecurringContinue}
                  style={{
                    width: "99px",
                    fontFamily: "'Work Sans', sans-serif",
                    borderRadius: "24px",
                    backgroundColor: disableRecurringContinue ? "#F1F0F5" : "#39A2DB",
                    height: "33px",
                    textTransform: "capitalize",
                    fontSize: "14px",
                    lineHeight: "18px",
                    color: "#fff",
                  }}
                  size="small"
                  onClick={handleContinue}
                  id="continueAPBtn"
                >
                  Continue
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  )
}